const toDateOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsedDate = value instanceof Date ? value : new Date(value);
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

const cloneBreaks = (arrBreaks = []) =>
  Array.isArray(arrBreaks)
    ? arrBreaks.map((objBreak = {}) => ({
        timBreakIn: toDateOrNull(objBreak.timBreakIn),
        timBreakOut: toDateOrNull(objBreak.timBreakOut),
      }))
    : [];

export const getEmptyAttendanceDoc = () => ({
  timCheckIn: null,
  timCheckOut: null,
  arrBreaks: [],
});

export const getOpenBreakIndex = (arrBreaks = []) =>
  arrBreaks.findIndex(
    (objBreak) => toDateOrNull(objBreak?.timBreakIn) && !toDateOrNull(objBreak?.timBreakOut)
  );

export const computeAttendanceStatus = (attendanceDoc = {}) => {
  const timCheckIn = toDateOrNull(attendanceDoc.timCheckIn);
  const timCheckOut = toDateOrNull(attendanceDoc.timCheckOut);
  const arrBreaks = cloneBreaks(attendanceDoc.arrBreaks);
  const intOpenBreakIndex = getOpenBreakIndex(arrBreaks);
  const hasCheckIn = Boolean(timCheckIn);
  const hasCheckOut = Boolean(timCheckOut);
  const hasOpenBreak = intOpenBreakIndex !== -1;

  const strCurrentState = !hasCheckIn
    ? "NOT_CHECKED_IN"
    : hasCheckOut
    ? "CHECKED_OUT"
    : hasOpenBreak
    ? "ON_BREAK"
    : "CHECKED_IN";

  return {
    strCurrentState,
    canCheckIn: !hasCheckIn,
    canBreakIn: hasCheckIn && !hasCheckOut && !hasOpenBreak,
    canBreakOut: hasCheckIn && !hasCheckOut && hasOpenBreak,
    canCheckOut: hasCheckIn && !hasCheckOut,
  };
};

export const getInitialAttendanceState = () => {
  const objData = getEmptyAttendanceDoc();
  return {
    objData,
    objStatus: computeAttendanceStatus(objData),
  };
};

export const computeAttendanceDurations = (attendanceDoc = {}, now = new Date()) => {
  const timCheckIn = toDateOrNull(attendanceDoc.timCheckIn);
  if (!timCheckIn) {
    return {
      workDurationMs: 0,
      breakDurationMs: 0,
    };
  }

  const timNow = toDateOrNull(now) || new Date();
  const timCheckOut = toDateOrNull(attendanceDoc.timCheckOut);
  const timWorkEnd =
    timCheckOut && timCheckOut.getTime() <= timNow.getTime() ? timCheckOut : timCheckOut || timNow;

  if (timWorkEnd.getTime() <= timCheckIn.getTime()) {
    return {
      workDurationMs: 0,
      breakDurationMs: 0,
    };
  }

  const arrBreaks = cloneBreaks(attendanceDoc.arrBreaks);
  let breakDurationMs = 0;

  arrBreaks.forEach((objBreak = {}) => {
    const timBreakIn = toDateOrNull(objBreak.timBreakIn);
    if (!timBreakIn) return;

    const timBreakOut = toDateOrNull(objBreak.timBreakOut) || timWorkEnd;
    const timStart = timBreakIn.getTime() < timCheckIn.getTime() ? timCheckIn : timBreakIn;
    const timEnd = timBreakOut.getTime() > timWorkEnd.getTime() ? timWorkEnd : timBreakOut;

    if (timEnd.getTime() > timStart.getTime()) {
      breakDurationMs += timEnd.getTime() - timStart.getTime();
    }
  });

  const grossWorkDurationMs = timWorkEnd.getTime() - timCheckIn.getTime();
  return {
    breakDurationMs,
    workDurationMs: Math.max(grossWorkDurationMs - breakDurationMs, 0),
  };
};

export const formatDuration = (durationMs = 0) => {
  const safeDurationMs = Number.isFinite(durationMs) ? durationMs : 0;
  const totalSeconds = Math.max(Math.floor(safeDurationMs / 1000), 0);
  const hours = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};

export const applyOptimisticAttendanceAction = (
  attendanceDoc = {},
  strAction,
  timAction = new Date()
) => {
  const action = `${strAction || ""}`.trim().toUpperCase();
  const timEvent = toDateOrNull(timAction);
  if (!timEvent) return attendanceDoc;

  const objNextDoc = {
    ...getEmptyAttendanceDoc(),
    ...attendanceDoc,
    timCheckIn: toDateOrNull(attendanceDoc.timCheckIn),
    timCheckOut: toDateOrNull(attendanceDoc.timCheckOut),
    arrBreaks: cloneBreaks(attendanceDoc.arrBreaks),
  };

  const hasCheckIn = Boolean(objNextDoc.timCheckIn);
  const hasCheckOut = Boolean(objNextDoc.timCheckOut);
  const intOpenBreakIndex = getOpenBreakIndex(objNextDoc.arrBreaks);

  if (action === "CHECK_IN") {
    if (!hasCheckIn) {
      objNextDoc.timCheckIn = timEvent;
    }
    return objNextDoc;
  }

  if (!hasCheckIn || hasCheckOut) {
    return objNextDoc;
  }

  if (action === "BREAK_IN") {
    if (intOpenBreakIndex === -1) {
      objNextDoc.arrBreaks.push({
        timBreakIn: timEvent,
        timBreakOut: null,
      });
    }
    return objNextDoc;
  }

  if (action === "BREAK_OUT") {
    if (intOpenBreakIndex !== -1) {
      objNextDoc.arrBreaks[intOpenBreakIndex] = {
        ...objNextDoc.arrBreaks[intOpenBreakIndex],
        timBreakOut: timEvent,
      };
    }
    return objNextDoc;
  }

  if (action === "CHECK_OUT") {
    if (intOpenBreakIndex !== -1) {
      objNextDoc.arrBreaks[intOpenBreakIndex] = {
        ...objNextDoc.arrBreaks[intOpenBreakIndex],
        timBreakOut: timEvent,
      };
    }
    objNextDoc.timCheckOut = timEvent;
    return objNextDoc;
  }

  return objNextDoc;
};
